/**
 * ReplayEngine — drives playback of a MissionLog.
 *
 * Manages time, interpolates spatial state between 500ms snapshots,
 * tracks active effects, and yields new events each frame.
 */

import type {
  SpatialState,
  SpatialEntity,
  Vec3,
  AlertLevel,
} from "../../types/game";
import type {
  MissionLog,
  MissionEvent,
  SpatialSnapshotEvent,
  EffectEvent,
  DialogueEvent,
  SceneCommandEvent,
  AlertEvent,
  IntentEvent,
  StateChangeEvent,
  PlayerActionEvent,
  MissionEndEvent,
} from "../../systems/MissionLogger";
import type { ActiveEffect } from "../../systems/SceneCommandExecutor";

// ── Public types ──

export interface ReplayFrame {
  spatial: SpatialState;
  activeEffects: ActiveEffect[];
  newDialogue: DialogueEvent[];
  newEffects: EffectEvent[];
  newSceneCommands: SceneCommandEvent[];
  newIntents: IntentEvent[];
  newStateChanges: StateChangeEvent[];
  newPlayerActions: PlayerActionEvent[];
  alertLevel: AlertLevel;
  missionEnded: MissionEndEvent | null;
}

// ── Helpers ──

function hermite(t: number): number {
  return t * t * (3 - 2 * t);
}

function lerpVec3(a: Vec3, b: Vec3, t: number): Vec3 {
  return {
    x: a.x + (b.x - a.x) * t,
    y: a.y + (b.y - a.y) * t,
    z: a.z + (b.z - a.z) * t,
  };
}

function normalizeVec3(v: Vec3): Vec3 {
  const len = Math.sqrt(v.x * v.x + v.y * v.y + v.z * v.z) || 1;
  return { x: v.x / len, y: v.y / len, z: v.z / len };
}

// ── Engine ──

export class ReplayEngine {
  private log: MissionLog;
  private _currentTime = 0;
  private _isPlaying = false;
  private _speed = 1;
  private cursor = 0; // index into sorted events

  // Pre-sorted spatial snapshots for fast lookup
  private snapshots: SpatialSnapshotEvent[] = [];
  private snapshotIndex = 0;

  // Pre-extracted alert events for state tracking
  private alertEvents: AlertEvent[] = [];
  private _alertLevel: AlertLevel;

  // Active effects with remaining duration
  private _activeEffects: ActiveEffect[] = [];
  private effectIdCounter = 0;

  // Duration
  private _duration: number;

  // Mission end event (if reached)
  private _missionEnded: MissionEndEvent | null = null;

  constructor(log: MissionLog) {
    this.log = log;

    // Sort events by time (should already be sorted, but be safe)
    this.log.events.sort((a, b) => a.t - b.t);

    // Extract spatial snapshots
    this.snapshots = this.log.events.filter(
      (e): e is SpatialSnapshotEvent => e.type === "spatial"
    );

    // Extract alert events
    this.alertEvents = this.log.events.filter(
      (e): e is AlertEvent => e.type === "alert"
    );

    // Initial alert level from encounter state
    this._alertLevel = log.initialEncounter.alertLevel || "normal";

    // Duration = last event time, or endedAt - startedAt
    if (log.endedAt) {
      this._duration = log.endedAt - log.startedAt;
    } else if (this.log.events.length > 0) {
      this._duration = this.log.events[this.log.events.length - 1].t;
    } else {
      this._duration = 0;
    }
  }

  // ── Accessors ──

  get currentTime(): number { return this._currentTime; }
  get duration(): number { return this._duration; }
  get isPlaying(): boolean { return this._isPlaying; }
  get speed(): number { return this._speed; }
  get alertLevel(): AlertLevel { return this._alertLevel; }
  get activeEffects(): ActiveEffect[] { return this._activeEffects; }
  get missionLog(): MissionLog { return this.log; }

  // ── Playback control ──

  play() { this._isPlaying = true; }
  pause() { this._isPlaying = false; }
  toggle() { this._isPlaying = !this._isPlaying; }

  setSpeed(multiplier: number) {
    this._speed = Math.max(0.25, Math.min(8, multiplier));
  }

  seek(timeMs: number) {
    const target = Math.max(0, Math.min(this._duration, timeMs));
    const forward = target >= this._currentTime;

    this._currentTime = target;
    this._missionEnded = null;

    if (!forward) {
      // Seeking backward — reset cursor and rebuild state
      this.cursor = 0;
      this.snapshotIndex = 0;
      this._activeEffects = [];
      this._alertLevel = this.log.initialEncounter.alertLevel || "normal";

      // Walk alert events up to current time
      for (const ae of this.alertEvents) {
        if (ae.t <= this._currentTime) this._alertLevel = ae.level;
        else break;
      }
    }

    // Advance cursor to current time (skip yielding events during seek)
    while (this.cursor < this.log.events.length && this.log.events[this.cursor].t <= this._currentTime) {
      const e = this.log.events[this.cursor];
      if (e.type === "alert") this._alertLevel = e.level;
      if (e.type === "mission_end") this._missionEnded = e;
      this.cursor++;
    }

    // Advance snapshot index
    this.snapshotIndex = 0;
    for (let i = 0; i < this.snapshots.length; i++) {
      if (this.snapshots[i].t <= this._currentTime) this.snapshotIndex = i;
      else break;
    }
  }

  // ── Frame tick ──

  tick(deltaMs: number): ReplayFrame {
    const newDialogue: DialogueEvent[] = [];
    const newEffects: EffectEvent[] = [];
    const newSceneCommands: SceneCommandEvent[] = [];
    const newIntents: IntentEvent[] = [];
    const newStateChanges: StateChangeEvent[] = [];
    const newPlayerActions: PlayerActionEvent[] = [];

    if (this._isPlaying) {
      this._currentTime += deltaMs * this._speed;
      if (this._currentTime >= this._duration) {
        this._currentTime = this._duration;
        this._isPlaying = false;
      }
    }

    // Advance event cursor, collecting new events
    while (this.cursor < this.log.events.length && this.log.events[this.cursor].t <= this._currentTime) {
      const event = this.log.events[this.cursor];
      switch (event.type) {
        case "dialogue": newDialogue.push(event); break;
        case "effect":
          newEffects.push(event);
          this.spawnEffect(event);
          break;
        case "scene_command": newSceneCommands.push(event); break;
        case "intent": newIntents.push(event); break;
        case "state_change": newStateChanges.push(event); break;
        case "player_action": newPlayerActions.push(event); break;
        case "alert": this._alertLevel = event.level; break;
        case "mission_end": this._missionEnded = event; break;
      }
      this.cursor++;
    }

    // Update active effects — remove expired ones
    this._activeEffects = this._activeEffects.filter(
      e => (this._currentTime - e.startedAt) < e.duration * 1000
    );

    // Advance snapshot index
    while (
      this.snapshotIndex < this.snapshots.length - 1 &&
      this.snapshots[this.snapshotIndex + 1].t <= this._currentTime
    ) {
      this.snapshotIndex++;
    }

    return {
      spatial: this.interpolateSpatial(),
      activeEffects: this._activeEffects,
      newDialogue,
      newEffects,
      newSceneCommands,
      newIntents,
      newStateChanges,
      newPlayerActions,
      alertLevel: this._alertLevel,
      missionEnded: this._missionEnded,
    };
  }

  // ── Spatial interpolation ──

  private interpolateSpatial(): SpatialState {
    if (this.snapshots.length === 0) {
      return this.log.initialEncounter.spatialState;
    }

    const snap = this.snapshots[this.snapshotIndex];
    const next = this.snapshots[this.snapshotIndex + 1];

    if (!next) {
      // At or past last snapshot — return it directly
      return this.snapshotToSpatial(snap);
    }

    // Interpolate between snapshots
    const dt = next.t - snap.t;
    const t = dt > 0 ? hermite(Math.min(1, (this._currentTime - snap.t) / dt)) : 0;

    const playerPos = lerpVec3(snap.playerShip.position, next.playerShip.position, t);
    const playerHeading = normalizeVec3(lerpVec3(snap.playerShip.heading, next.playerShip.heading, t));

    // Interpolate entities
    const entities: SpatialEntity[] = [];
    const nextMap = new Map(next.entities.map(e => [e.id, e]));

    for (const e of snap.entities) {
      const n = nextMap.get(e.id);
      const pos = n ? lerpVec3(e.position, n.position, t) : e.position;

      // Find full entity data from initial encounter for visuals
      const initialEntity = this.log.initialEncounter.spatialState.entities.find(ie => ie.id === e.id);

      entities.push({
        id: e.id,
        name: initialEntity?.name ?? e.id,
        type: initialEntity?.type ?? "ship",
        position: pos,
        faction: initialEntity?.faction,
        shipClass: initialEntity?.shipClass,
        shields: e.shields,
        condition: e.condition as SpatialEntity["condition"],
        cloaked: e.cloaked,
        planetVisuals: initialEntity?.planetVisuals,
        starData: initialEntity?.starData,
      });
    }

    // Include entities only in next snapshot (newly spawned)
    for (const n of next.entities) {
      if (!snap.entities.some(e => e.id === n.id)) {
        const initialEntity = this.log.initialEncounter.spatialState.entities.find(ie => ie.id === n.id);
        entities.push({
          id: n.id,
          name: initialEntity?.name ?? n.id,
          type: initialEntity?.type ?? "ship",
          position: n.position,
          faction: initialEntity?.faction,
          shipClass: initialEntity?.shipClass,
          shields: n.shields,
          condition: n.condition as SpatialEntity["condition"],
          cloaked: n.cloaked,
          planetVisuals: initialEntity?.planetVisuals,
          starData: initialEntity?.starData,
        });
      }
    }

    return {
      playerShip: {
        position: playerPos,
        heading: playerHeading,
        speed: next.playerShip.speed,
        shields: snap.playerShip.shields,
        orbitTarget: snap.playerShip.orbitTarget,
      },
      entities,
    };
  }

  private snapshotToSpatial(snap: SpatialSnapshotEvent): SpatialState {
    return {
      playerShip: {
        position: snap.playerShip.position,
        heading: snap.playerShip.heading,
        speed: snap.playerShip.speed,
        shields: snap.playerShip.shields,
        orbitTarget: snap.playerShip.orbitTarget,
      },
      entities: snap.entities.map(e => {
        const initial = this.log.initialEncounter.spatialState.entities.find(ie => ie.id === e.id);
        return {
          id: e.id,
          name: initial?.name ?? e.id,
          type: initial?.type ?? "ship",
          position: e.position,
          faction: initial?.faction,
          shipClass: initial?.shipClass,
          shields: e.shields,
          condition: e.condition as SpatialEntity["condition"],
          cloaked: e.cloaked,
          planetVisuals: initial?.planetVisuals,
          starData: initial?.starData,
        };
      }),
    };
  }

  // ── Effect spawning ──

  private spawnEffect(event: EffectEvent) {
    const eff = event.effect;
    // Ensure unique IDs — logs can have duplicates (e.g. tractor re-engaged)
    let id = eff.id || `replay_effect_${this.effectIdCounter++}`;
    if (this._activeEffects.some(e => e.id === id)) {
      id = `${id}_${this.effectIdCounter++}`;
    }
    this._activeEffects.push({
      id,
      type: eff.effectType,
      from: eff.from,
      to: eff.to,
      position: eff.position,
      color: eff.color,
      duration: eff.duration,
      size: eff.size,
      startedAt: this._currentTime,
    });
  }

  // ── Utility ──

  /** Get all events of a given type for building chapter markers */
  getEventsOfType<T extends MissionEvent["type"]>(type: T): Extract<MissionEvent, { type: T }>[] {
    return this.log.events.filter(e => e.type === type) as any;
  }

  /** Get the initial encounter state */
  get initialEncounter() {
    return this.log.initialEncounter;
  }
}
